import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Download, MailPlus, MoreHorizontal, Plus, Trash2, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import {
  BUSINESS_TYPE_LABELS,
  CUSTOM_FIELD_ENTITIES,
  CUSTOM_FIELD_TYPES,
  ROLES,
  ROLE_LABELS,
  createCustomFieldInput,
  slug as slugSchema,
  updateBrandingInput,
  updateSettingsInput,
  type MemberRole,
} from '@buildbee/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Field,
  Form,
  FormActions,
  FormError,
  FormField,
  Input,
  LoadingState,
  PageHeader,
  Select,
  Status,
  Textarea,
  cn,
  formatDateTime,
  formatRelative,
  titleCase,
  useToast,
  useZodForm,
  type ColumnDef,
  segmentedItemClass,
  segmentedListClass,
} from '@buildbee/ui';
import {
  canadianSalesTax,
  formatTaxRate,
  normalizeRegion,
  regionLabel,
  regionsFor,
  ROLE_DESCRIPTIONS,
  SUPPORTED_COUNTRIES,
} from '@buildbee/domain';
import { CURRENCY_OPTIONS, LOCALE_OPTIONS, TIMEZONE_OPTIONS } from '../../../lib/regional';
import { statusTone } from '../../status-tone';
import { apiBase, getWorkspaceSlug } from '../../../lib/workspace';
import { errorMessage, trpc, type RouterOutputs } from '../../../lib/trpc';
import { BrandedLoginLinkCard } from '../auth/branded';
import { InviteDialog, roleOptionsFor } from './invite';
import { settingsNavFor, type SettingsNavGroup } from './nav';

// ---------------------------------------------------------------------------
// Settings layout
// ---------------------------------------------------------------------------

export function SettingsLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = trpc.workspace.current.useQuery();
  const mine = trpc.workspace.roles.mine.useQuery();
  const groups = settingsNavFor(current.data?.membership.role ?? null, mine.data?.cells ?? null);
  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to || pathname === `${to}/` : pathname.startsWith(to);
  return (
    <div>
      <PageHeader title="Settings" description="Your company, your team and your data." />
      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-8">
        <SettingsNav groups={groups} isActive={isActive} />
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function SettingsNav({
  groups,
  isActive,
}: {
  groups: SettingsNavGroup[];
  isActive: (to: string, exact?: boolean) => boolean;
}) {
  return (
    <nav aria-label="Settings">
      {/* Phones and tablets: one scrolling row. */}
      <div className={cn(segmentedListClass, 'lg:hidden')}>
        {groups
          .flatMap((g) => g.links)
          .map((l) => (
            <Link key={l.to} to={l.to} className={segmentedItemClass(isActive(l.to, l.exact))}>
              {l.label}
            </Link>
          ))}
      </div>
      {/* Desktop: grouped list. */}
      <div className="hidden space-y-5 lg:block">
        {groups.map((g) => (
          <div key={g.label}>
            <p className="mb-1 px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {g.label}
            </p>
            <ul className="space-y-0.5">
              {g.links.map((l) => {
                const active = isActive(l.to, l.exact);
                return (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'block rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground',
                        active && 'bg-foreground/[0.06] font-semibold text-foreground',
                      )}
                    >
                      {l.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

function useCurrent() {
  return trpc.workspace.current.useQuery();
}

// ---------------------------------------------------------------------------
// General
// ---------------------------------------------------------------------------

const generalSchema = z.object({ name: z.string().trim().min(1).max(120), slug: slugSchema });

export function GeneralSettingsPage() {
  const current = useCurrent();
  if (current.isLoading) return <LoadingState />;
  if (current.error || !current.data)
    return <ErrorState error={current.error} onRetry={() => void current.refetch()} />;
  return (
    <div className="space-y-6">
      <WorkspaceIdentityCard
        ws={current.data.workspace}
        canEdit={['owner', 'admin'].includes(current.data.membership.role)}
      />
      <WorkspaceDefaultsCard
        settings={current.data.workspace.settings}
        canEdit={['owner', 'admin'].includes(current.data.membership.role)}
      />
    </div>
  );
}

type CurrentWorkspace = RouterOutputs['workspace']['current']['workspace'];

function WorkspaceIdentityCard({ ws, canEdit }: { ws: CurrentWorkspace; canEdit: boolean }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const update = trpc.workspace.update.useMutation({
    onSuccess: async () => {
      toast.success('Workspace updated');
      await Promise.all([utils.workspace.current.invalidate(), utils.workspace.me.get.invalidate()]);
    },
  });
  const form = useZodForm(generalSchema, { values: { name: ws.name, slug: ws.slug } });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Company details</CardTitle>
        <CardDescription>
          {BUSINESS_TYPE_LABELS[ws.businessType]} · created {formatRelative(ws.createdAt)}
        </CardDescription>
      </CardHeader>
      <Form
        form={form}
        onSubmit={async (values) => {
          const slugChanged = values.slug !== ws.slug;
          await update.mutateAsync(values);
          if (slugChanged) {
            localStorage.setItem('bb-workspace', values.slug);
            window.location.assign('/settings');
          }
        }}
      >
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <FormField name="name" label="Company name">
            <Input disabled={!canEdit} />
          </FormField>
          <FormField
            name="slug"
            label="Company link name"
            description="Used in your team sign-in link (/c/<link name>) and client portal link. Changing it breaks links you have already shared."
          >
            <Input disabled={!canEdit} />
          </FormField>
          <Field
            label="Company code"
            htmlFor="ws-company-code"
            description="Your team and clients can type this on the sign-in page to open your company. To change it, contact BuildBee support."
          >
            <Input id="ws-company-code" value={ws.companyCode ?? ''} readOnly className="figures" />
          </Field>
          <FormError message={update.error ? errorMessage(update.error) : null} />
        </CardContent>
        {canEdit ? (
          <CardFooter>
            <Button type="submit" loading={update.isPending} disabled={!form.formState.isDirty}>
              Save
            </Button>
          </CardFooter>
        ) : null}
      </Form>
    </Card>
  );
}

const defaultsSchema = updateSettingsInput;

type TaxComponentsData = RouterOutputs['workspace']['settings']['taxComponents'];

/**
 * GST/HST/PST/QST for the chosen province: the saved workspace's from `workspace.settings.taxComponents`,
 * or a preview for a province picked but not saved yet.
 */
function useTaxBreakdown(
  saved: TaxComponentsData | undefined,
  country: string | undefined,
  taxRegion: string | null | undefined,
) {
  return useMemo(() => {
    const c = (country ?? '').toUpperCase();
    const r = taxRegion ? taxRegion.toUpperCase() : null;
    if (saved && saved.country === c && (saved.taxRegion ?? null) === r)
      return { components: saved.components, totalRateBps: saved.totalRateBps };
    const tax = c === 'CA' && r ? canadianSalesTax(r) : null;
    return { components: tax?.components ?? [], totalRateBps: tax?.totalRateBps ?? null };
  }, [saved, country, taxRegion]);
}

function withCurrent(
  options: readonly { value: string; label: string }[],
  current: string | null | undefined,
) {
  return current && !options.some((o) => o.value === current)
    ? [...options, { value: current, label: current }]
    : [...options];
}

function WorkspaceDefaultsCard({
  settings,
  canEdit,
}: {
  settings: CurrentWorkspace['settings'];
  canEdit: boolean;
}) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const update = trpc.workspace.settings.update.useMutation({
    onSuccess: async () => {
      toast.success('Settings saved');
      await Promise.all([
        utils.workspace.current.invalidate(),
        utils.workspace.settings.taxComponents.invalidate(),
      ]);
    },
  });
  const taxComponents = trpc.workspace.settings.taxComponents.useQuery();
  const form = useZodForm(defaultsSchema, { values: settings });
  const pct = (bps: number | undefined) => ((bps ?? 0) / 100).toString();
  const [taxPct, setTaxPct] = useState(() => pct(settings.defaultTaxRateBps));
  const country = form.watch('country') ?? settings.country;
  const taxRegion = form.watch('taxRegion');
  const taxRate = form.watch('defaultTaxRateBps');
  const breakdown = useTaxBreakdown(taxComponents.data, country, taxRegion);
  const provincialBps = breakdown.totalRateBps === null ? null : Math.round(breakdown.totalRateBps);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Defaults</CardTitle>
        <CardDescription>Used for new estimates, invoices and projects.</CardDescription>
      </CardHeader>
      <Form form={form} onSubmit={(values) => update.mutateAsync(values)}>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <Field label="Country" htmlFor="ws-set-country">
            <Select
              id="ws-set-country"
              disabled={!canEdit}
              value={country}
              onChange={(e) => {
                const next = e.target.value;
                form.setValue('country', next, { shouldDirty: true });
                if (taxRegion && !normalizeRegion(next, taxRegion))
                  form.setValue('taxRegion', null, { shouldDirty: true });
              }}
              options={withCurrent(
                SUPPORTED_COUNTRIES.map((c) => ({ value: c.code, label: c.name })),
                country,
              )}
            />
          </Field>
          <Field
            label={`Tax ${regionLabel(country).toLowerCase()}`}
            htmlFor="ws-set-tax-region"
            error={form.formState.errors.taxRegion?.message}
          >
            <Select
              id="ws-set-tax-region"
              disabled={!canEdit}
              value={taxRegion ?? ''}
              onChange={(e) => form.setValue('taxRegion', e.target.value || null, { shouldDirty: true })}
              options={[
                { value: '', label: 'Not set' },
                ...withCurrent(
                  regionsFor(country).map((r) => ({ value: r.code, label: r.name })),
                  taxRegion,
                ),
              ]}
            />
          </Field>
          <FormField name="currency" label="Currency">
            <Select disabled={!canEdit} options={withCurrent(CURRENCY_OPTIONS, settings.currency)} />
          </FormField>
          <FormField name="locale" label="Locale" description="Number and date formats">
            <Select disabled={!canEdit} options={withCurrent(LOCALE_OPTIONS, settings.locale)} />
          </FormField>
          <FormField name="timezone" label="Time zone">
            <Select disabled={!canEdit} options={withCurrent(TIMEZONE_OPTIONS, settings.timezone)} />
          </FormField>
          <FormField name="paymentTermsDays" label="Payment terms (days)">
            <Input
              type="number"
              disabled={!canEdit}
              {...form.register('paymentTermsDays', { valueAsNumber: true })}
            />
          </FormField>
          <Field label="Default markup (%)">
            <Input
              type="number"
              step="0.01"
              disabled={!canEdit}
              defaultValue={pct(settings.defaultMarkupBps)}
              onChange={(e) =>
                form.setValue('defaultMarkupBps', Math.round(Number(e.target.value) * 100), {
                  shouldDirty: true,
                })
              }
            />
          </Field>
          <div className="space-y-2">
            <Field label="Default tax rate (%)" htmlFor="ws-set-tax-rate">
              <Input
                id="ws-set-tax-rate"
                type="number"
                step="0.01"
                disabled={!canEdit}
                value={taxPct}
                onChange={(e) => {
                  setTaxPct(e.target.value);
                  form.setValue('defaultTaxRateBps', Math.round(Number(e.target.value) * 100), {
                    shouldDirty: true,
                  });
                }}
              />
            </Field>
            {breakdown.components.length ? (
              <div role="group" aria-label="Sales tax breakdown" className="space-y-1.5 text-xs">
                <ul className="space-y-0.5 text-muted-foreground">
                  {breakdown.components.map((c) => (
                    <li key={c.code} className="flex justify-between gap-2">
                      <span>{c.name}</span>
                      <span className="figures">{c.label}</span>
                    </li>
                  ))}
                  {breakdown.components.length > 1 && breakdown.totalRateBps !== null ? (
                    <li className="flex justify-between gap-2 font-medium text-foreground">
                      <span>Total</span>
                      <span className="figures">{formatTaxRate(breakdown.totalRateBps)}</span>
                    </li>
                  ) : null}
                </ul>
                {canEdit && provincialBps !== null ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={taxRate === provincialBps}
                    onClick={() => {
                      form.setValue('defaultTaxRateBps', provincialBps, { shouldDirty: true });
                      setTaxPct(pct(provincialBps));
                    }}
                  >
                    Use provincial rate
                  </Button>
                ) : null}
              </div>
            ) : country.toUpperCase() === 'CA' && !taxRegion ? (
              <p className="text-xs text-muted-foreground">
                Choose a tax province to see its GST/HST/PST/QST.
              </p>
            ) : null}
          </div>
          <Field label="Default retainage (%)">
            <Input
              type="number"
              step="0.01"
              disabled={!canEdit}
              defaultValue={pct(settings.defaultRetainageBps)}
              onChange={(e) =>
                form.setValue('defaultRetainageBps', Math.round(Number(e.target.value) * 100), {
                  shouldDirty: true,
                })
              }
            />
          </Field>
          <FormField name="projectPrefix" label="Project number prefix">
            <Input disabled={!canEdit} />
          </FormField>
          <FormField name="estimatePrefix" label="Estimate prefix">
            <Input disabled={!canEdit} />
          </FormField>
          <FormField name="invoicePrefix" label="Invoice prefix">
            <Input disabled={!canEdit} />
          </FormField>
          <div className="space-y-3 sm:col-span-3">
            <Checkbox
              label="Client portal enabled"
              disabled={!canEdit}
              checked={form.watch('portalEnabled') ?? true}
              onCheckedChange={(v) => form.setValue('portalEnabled', v === true, { shouldDirty: true })}
            />
            <Checkbox
              label="Clients can pay invoices in the portal (card and bank debit via Stripe)"
              disabled={!canEdit}
              checked={form.watch('portalPaymentsEnabled') ?? true}
              onCheckedChange={(v) =>
                form.setValue('portalPaymentsEnabled', v === true, { shouldDirty: true })
              }
            />
          </div>
          <FormError message={update.error ? errorMessage(update.error) : null} />
        </CardContent>
        {canEdit ? (
          <CardFooter>
            <Button type="submit" loading={update.isPending} disabled={!form.formState.isDirty}>
              Save defaults
            </Button>
          </CardFooter>
        ) : null}
      </Form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Branding
// ---------------------------------------------------------------------------

export function BrandingSettingsPage() {
  const branding = trpc.workspace.branding.get.useQuery();
  const utils = trpc.useUtils();
  const toast = useToast();
  const update = trpc.workspace.branding.update.useMutation({
    onSuccess: async () => {
      toast.success('Branding saved');
      await Promise.all([utils.workspace.branding.get.invalidate(), utils.workspace.current.invalidate()]);
    },
  });
  const requestUpload = trpc.workspace.branding.requestLogoUpload.useMutation();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const form = useZodForm(updateBrandingInput, { values: branding.data?.branding ?? {} });

  if (branding.isLoading) return <LoadingState />;
  if (branding.error || !branding.data)
    return <ErrorState error={branding.error} onRetry={() => void branding.refetch()} />;

  const onLogo = async (file: File) => {
    const allowed = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'] as const;
    const type = allowed.find((t) => t === file.type);
    if (!type) return toast.error('Unsupported image', 'Use PNG, JPEG, SVG or WebP.');
    if (file.size > 2 * 1024 * 1024) return toast.error('Logo too large', 'Keep it under 2 MB.');
    setUploading(true);
    try {
      const { key, uploadUrl } = await requestUpload.mutateAsync({ fileName: file.name, contentType: type });
      const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'content-type': type } });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      await update.mutateAsync({ ...form.getValues(), logoKey: key });
    } catch (err) {
      toast.error('Logo upload failed', errorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <BrandedLoginLinkCard />
      <Card>
        <CardHeader>
          <CardTitle>Branding</CardTitle>
          <CardDescription>
            Shown on your team sign-in page, the client portal, proposals, invoices and emails.
          </CardDescription>
        </CardHeader>
        <Form form={form} onSubmit={(values) => update.mutateAsync(values)}>
          <CardContent className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="flex size-20 items-center justify-center overflow-hidden rounded-lg bg-muted">
                {branding.data.logoUrl ? (
                  <img
                    src={branding.data.logoUrl}
                    alt="Workspace logo"
                    className="size-full object-contain"
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">No logo</span>
                )}
              </div>
              <div className="space-y-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void onLogo(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  loading={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload /> Upload logo
                </Button>
                {branding.data.branding.logoKey ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => update.mutate({ ...form.getValues(), logoKey: null })}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField name="primaryColor" label="Primary color" description="#RRGGBB">
                <Input placeholder="#f59e0b" />
              </FormField>
              <FormField name="accentColor" label="Accent color" description="#RRGGBB">
                <Input placeholder="#1f2937" />
              </FormField>
            </div>
            <FormField name="portalWelcome" label="Portal welcome message">
              <Textarea rows={3} />
            </FormField>
            <FormField name="emailFooter" label="Email footer">
              <Textarea rows={2} />
            </FormField>
            <FormError message={update.error ? errorMessage(update.error) : null} />
          </CardContent>
          <CardFooter>
            <Button type="submit" loading={update.isPending} disabled={!form.formState.isDirty}>
              Save branding
            </Button>
          </CardFooter>
        </Form>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Members + invitations
// ---------------------------------------------------------------------------

type MemberRow = RouterOutputs['workspace']['members']['list'][number];

export function MembersSettingsPage() {
  const current = useCurrent();
  const members = trpc.workspace.members.list.useQuery();
  const invitations = trpc.workspace.invitations.list.useQuery();
  const assignable = trpc.workspace.roles.assignable.useQuery();
  const templates = trpc.workspace.roles.templates.list.useQuery();
  const utils = trpc.useUtils();
  const toast = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removing, setRemoving] = useState<MemberRow | null>(null);
  const [changingRole, setChangingRole] = useState<{ member: MemberRow; role: MemberRole } | null>(null);
  const mine = trpc.workspace.roles.mine.useQuery();

  const refresh = () =>
    Promise.all([utils.workspace.members.list.invalidate(), utils.workspace.invitations.list.invalidate()]);
  const updateMember = trpc.workspace.members.update.useMutation({
    onSuccess: async () => {
      toast.success('Member updated');
      setChangingRole(null);
      await refresh();
    },
    onError: (e) => toast.error('Could not update member', errorMessage(e)),
  });
  const removeMember = trpc.workspace.members.remove.useMutation({
    onSuccess: async () => {
      toast.success('Member removed');
      setRemoving(null);
      await refresh();
    },
    onError: (e) => toast.error('Could not remove member', errorMessage(e)),
  });
  const revoke = trpc.workspace.invitations.revoke.useMutation({ onSuccess: refresh });
  const resend = trpc.workspace.invitations.resend.useMutation({
    onSuccess: () => toast.success('Invitation re-sent'),
    onError: (e) => toast.error('Could not resend', errorMessage(e)),
  });

  const canManage = !!assignable.data?.length && !!mine.data?.cells.includes('member:manage');
  const me = current.data?.membership.id;
  const roleOptions = roleOptionsFor((assignable.data ?? []) as MemberRole[]);

  const columns = useMemo<ColumnDef<MemberRow, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Member',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <Avatar name={row.original.name} src={row.original.image} size="sm" />
            <div className="min-w-0">
              <p className="truncate font-medium">
                {row.original.name}{' '}
                {row.original.id === me ? <span className="text-xs text-muted-foreground">(you)</span> : null}
              </p>
              <p className="truncate text-xs text-muted-foreground">{row.original.email}</p>
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Role',
        filterFn: 'equalsString',
        cell: ({ row }) =>
          canManage && row.original.id !== me && (assignable.data ?? []).includes(row.original.role) ? (
            <Select
              aria-label={`Role for ${row.original.name}`}
              className="h-8 w-52"
              value={row.original.role}
              options={roleOptions}
              onChange={(e) => {
                const next = e.target.value as MemberRole;
                if (next !== row.original.role) setChangingRole({ member: row.original, role: next });
              }}
            />
          ) : (
            <Badge>{ROLE_LABELS[row.original.role]}</Badge>
          ),
      },
      {
        accessorKey: 'permissionTemplateId',
        header: 'Exception',
        enableSorting: false,
        cell: ({ row }) =>
          canManage && row.original.id !== me ? (
            <Select
              aria-label={`Exception for ${row.original.name}`}
              className="h-8 w-48"
              value={row.original.permissionTemplateId ?? ''}
              onChange={(e) =>
                updateMember.mutate({ id: row.original.id, permissionTemplateId: e.target.value || null })
              }
              options={[
                { value: '', label: 'None' },
                ...(templates.data ?? [])
                  .filter((t) => t.baseRole === row.original.role)
                  .map((t) => ({ value: t.id, label: t.name })),
              ]}
            />
          ) : (
            <span className="pl-3 text-sm text-muted-foreground">
              {templates.data?.find((t) => t.id === row.original.permissionTemplateId)?.name ?? 'None'}
            </span>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Status tone={statusTone(row.original.status)}>{titleCase(row.original.status)}</Status>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          canManage && row.original.id !== me ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Member actions">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onSelect={() =>
                    updateMember.mutate({
                      id: row.original.id,
                      status: row.original.status === 'active' ? 'suspended' : 'active',
                    })
                  }
                >
                  {row.original.status === 'active' ? 'Suspend access' : 'Restore access'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem destructive icon={<Trash2 />} onSelect={() => setRemoving(row.original)}>
                  Remove from team
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, me, assignable.data, templates.data, roleOptions],
  );

  const pending = (invitations.data ?? []).filter((i) => i.status === 'pending' || i.status === 'expired');

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[17px] font-semibold tracking-[-0.02em]">Team members</h2>
          {canManage ? (
            <Button onClick={() => setInviteOpen(true)}>
              <MailPlus /> Invite
            </Button>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          What each role can do is set on{' '}
          <Link to="/settings/roles" className="font-medium text-foreground underline underline-offset-2">
            Roles & permissions
          </Link>
          . Suspending someone stops them signing in but keeps their work; removing takes them off the team.
          Clients are invited from Client portal.
        </p>
        <DataTable
          columns={columns}
          data={members.data}
          loading={members.isLoading}
          error={members.error}
          onRetry={() => void members.refetch()}
          searchPlaceholder="Search members"
          filters={[
            {
              columnId: 'role',
              label: 'Roles',
              options: ROLES.filter((r) => r !== 'client').map((r) => ({ value: r, label: ROLE_LABELS[r] })),
            },
          ]}
          initialSorting={[{ id: 'name', desc: false }]}
          getRowId={(r) => r.id}
        />
      </section>

      <section className="space-y-3">
        <h2 className="text-[17px] font-semibold tracking-[-0.02em]">Pending invitations</h2>
        {invitations.isLoading ? (
          <LoadingState />
        ) : pending.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending invitations. People you invite show here until they accept.</p>
        ) : (
          <Card>
            <ul className="divide-y">
              {pending.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{i.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {ROLE_LABELS[i.role]} · invited by {i.invitedByName ?? 'unknown'} ·{' '}
                      {formatRelative(i.createdAt)}
                    </p>
                  </div>
                  <Status tone={statusTone(i.status)}>{titleCase(i.status)}</Status>
                  {canManage ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        loading={resend.isPending && resend.variables?.id === i.id}
                        onClick={() => resend.mutate({ id: i.id })}
                      >
                        Resend
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => revoke.mutate({ id: i.id })}>
                        Revoke
                      </Button>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        roles={(assignable.data ?? []) as MemberRole[]}
        onDone={refresh}
      />
      <ConfirmDialog
        open={!!changingRole}
        onOpenChange={(o) => !o && setChangingRole(null)}
        title={
          changingRole
            ? `Make ${changingRole.member.name} ${ROLE_LABELS[changingRole.role]}?`
            : 'Change role?'
        }
        description={
          changingRole
            ? `${ROLE_DESCRIPTIONS[changingRole.role]} The change takes effect right away.`
            : undefined
        }
        confirmLabel="Change role"
        loading={updateMember.isPending}
        onConfirm={() =>
          changingRole && updateMember.mutate({ id: changingRole.member.id, role: changingRole.role })
        }
      />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove from the team?"
        description={
          removing
            ? `${removing.name} loses access right away. Their past work stays in your records.`
            : undefined
        }
        confirmLabel="Remove"
        destructive
        loading={removeMember.isPending}
        onConfirm={() => removing && removeMember.mutate({ id: removing.id })}
      />
    </div>
  );
}

// Roles & permissions: see ./roles.tsx
export { RolesSettingsPage } from './roles';

// ---------------------------------------------------------------------------
// Custom fields
// ---------------------------------------------------------------------------

export function CustomFieldsSettingsPage() {
  const [entity, setEntity] = useState<(typeof CUSTOM_FIELD_ENTITIES)[number]>('contact');
  const fields = trpc.workspace.customFields.list.useQuery({ entity });
  const utils = trpc.useUtils();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const archive = trpc.workspace.customFields.archive.useMutation({
    onSuccess: () => utils.workspace.customFields.list.invalidate(),
    onError: (e) => toast.error('Could not archive field', errorMessage(e)),
  });
  const create = trpc.workspace.customFields.create.useMutation({
    onSuccess: async () => {
      toast.success('Field added');
      setOpen(false);
      await utils.workspace.customFields.list.invalidate();
    },
  });
  const form = useZodForm(createCustomFieldInput, {
    defaultValues: { entity, key: '', label: '', type: 'text', options: [], required: false, position: 0 },
  });
  const [optionsText, setOptionsText] = useState('');
  const type = form.watch('type');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-56">
          <Select
            aria-label="Record type"
            value={entity}
            onChange={(e) => setEntity(e.target.value as typeof entity)}
            options={CUSTOM_FIELD_ENTITIES.map((e) => ({ value: e, label: titleCase(e) }))}
          />
        </div>
        <Button
          onClick={() => {
            form.reset({
              entity,
              key: '',
              label: '',
              type: 'text',
              options: [],
              required: false,
              position: fields.data?.length ?? 0,
            });
            setOptionsText('');
            setOpen(true);
          }}
        >
          <Plus /> Add field
        </Button>
      </div>
      {fields.isLoading ? (
        <LoadingState />
      ) : fields.error ? (
        <ErrorState error={fields.error} onRetry={() => void fields.refetch()} />
      ) : !fields.data?.length ? (
        <EmptyState
          title={`No custom fields on ${titleCase(entity).toLowerCase()}s`}
          description="Track anything extra, like lot numbers, permit IDs or lead temperature."
        />
      ) : (
        <Card>
          <ul className="divide-y">
            {fields.data.map((f) => (
              <li key={f.id} className="flex items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {f.label} {f.required ? <Badge variant="outline">Required</Badge> : null}
                  </p>
                  <p className="figures text-xs text-muted-foreground">
                    {f.key} · {f.type}
                    {f.options.length ? ` · ${f.options.join(', ')}` : ''}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => archive.mutate({ id: f.id })}>
                  Archive
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title="Add custom field">
          <Form
            form={form}
            onSubmit={(v) =>
              create.mutateAsync({
                ...v,
                entity,
                options: ['select', 'multiselect'].includes(v.type)
                  ? optionsText
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : [],
              })
            }
          >
            <FormField name="label" label="Label" required>
              <Input
                onChange={(e) => {
                  form.setValue('label', e.target.value);
                  if (!form.formState.dirtyFields.key) {
                    form.setValue(
                      'key',
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '_')
                        .replace(/^_+|_+$/g, '')
                        .replace(/^(\d)/, 'f_$1'),
                    );
                  }
                }}
              />
            </FormField>
            <FormField name="key" label="Key" description="snake_case, cannot be changed later.">
              <Input className="font-mono" />
            </FormField>
            <FormField name="type" label="Type">
              <Select options={CUSTOM_FIELD_TYPES.map((t) => ({ value: t, label: titleCase(t) }))} />
            </FormField>
            {type === 'select' || type === 'multiselect' ? (
              <Field label="Options" description="One per line.">
                <Textarea rows={4} value={optionsText} onChange={(e) => setOptionsText(e.target.value)} />
              </Field>
            ) : null}
            <Checkbox
              label="Required"
              checked={form.watch('required')}
              onCheckedChange={(c) => form.setValue('required', c === true)}
            />
            <FormError message={create.error ? errorMessage(create.error) : null} />
            <FormActions>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Add field
              </Button>
            </FormActions>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

type AuditRow = RouterOutputs['workspace']['audit']['list']['items'][number];

export function AuditLogPage() {
  const [action, setAction] = useState('');
  const query = trpc.workspace.audit.list.useInfiniteQuery(
    { limit: 50, action: action || undefined },
    { getNextPageParam: (last) => last.nextCursor },
  );
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const columns = useMemo<ColumnDef<AuditRow, unknown>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'When',
        cell: ({ row }) => (
          <span title={formatDateTime(row.original.createdAt)}>{formatRelative(row.original.createdAt)}</span>
        ),
      },
      {
        accessorKey: 'actorName',
        header: 'Who',
        cell: ({ row }) =>
          // Changes BuildBee staff make from the admin console show as support, not by staff name.
          (row.original.metadata as Record<string, unknown> | null)?.byPlatformAdmin === true ? (
            'BuildBee support'
          ) : (
            row.original.actorName ?? <span className="text-muted-foreground">{titleCase(row.original.actorType)}</span>
          ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }) => <code className="text-xs">{row.original.action}</code>,
      },
      { accessorKey: 'entityType', header: 'Record', cell: ({ row }) => titleCase(row.original.entityType) },
      {
        id: 'changes',
        header: 'Changes',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.changes ? (
            <span className="text-xs text-muted-foreground">
              {Object.keys(row.original.changes).join(', ')}
            </span>
          ) : null,
      },
    ],
    [],
  );
  return (
    <DataTable
      columns={columns}
      data={rows}
      loading={query.isLoading}
      error={query.error}
      onRetry={() => void query.refetch()}
      searchable={false}
      paginate={false}
      empty={{ title: 'No activity yet', description: 'Every change in the workspace is recorded here.' }}
      toolbar={
        <Input
          className="w-56"
          placeholder="Filter by action (e.g. deal.)"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          aria-label="Filter by action"
        />
      }
      footer={
        query.hasNextPage ? (
          <div className="flex justify-center">
            <Button
              variant="outline"
              loading={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        ) : null
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function ExportSettingsPage() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const slug = getWorkspaceSlug();
      const res = await fetch(`${apiBase}/v1/workspace/export`, {
        credentials: 'include',
        headers: slug ? { 'x-workspace-slug': slug } : {},
      });
      if (!res.ok)
        throw new Error(
          ((await res.json().catch(() => null)) as { error?: string } | null)?.error ??
            `Export failed (${res.status})`,
        );
      const blob = await res.blob();
      const name =
        /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
        'buildbee-export.json';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Export failed', errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Export workspace data</CardTitle>
        <CardDescription>
          Download every record in this workspace as JSON: contacts, deals, projects, financials, documents
          metadata and the audit log. Files stay in storage; their keys are included. Only owners and admins
          can export.
        </CardDescription>
      </CardHeader>
      <CardFooter>
        <Button onClick={() => void download()} loading={busy}>
          <Download /> Download export
        </Button>
      </CardFooter>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export function ProfileSettingsPage() {
  const me = trpc.workspace.me.get.useQuery();
  const utils = trpc.useUtils();
  const toast = useToast();
  const update = trpc.workspace.me.update.useMutation({
    onSuccess: async () => {
      toast.success('Profile updated');
      await utils.workspace.me.get.invalidate();
    },
  });
  const form = useZodForm(z.object({ name: z.string().trim().min(1).max(120) }), {
    values: { name: me.data?.user.name ?? '' },
  });
  if (me.isLoading) return <LoadingState />;
  if (!me.data) return <ErrorState error={me.error} />;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Your profile</CardTitle>
        <CardDescription>{me.data.user.email}</CardDescription>
      </CardHeader>
      <Form form={form} onSubmit={(v) => update.mutateAsync(v)}>
        <CardContent>
          <FormField name="name" label="Name">
            <Input />
          </FormField>
        </CardContent>
        <CardFooter>
          <Button type="submit" loading={update.isPending} disabled={!form.formState.isDirty}>
            Save
          </Button>
        </CardFooter>
      </Form>
    </Card>
  );
}
